import { jsx } from 'panel';
import partition from 'lodash/partition';
import sumBy from 'lodash/sumBy';
import { CheckboxEl } from 'common/components/checkbox';
import { TooltipEl } from 'common/components/tooltip';
import { SvgIconEl } from 'common/components/svg-icon';
import { Placement } from 'common/components/tooltip/types'; //#region helper mixins

function ColumnTitle(_ref) {
  let {
    context
  } = _ref;
  return jsx("div", {
    sel: '.text'
  }, context.title);
} // eslint-disable-next-line sonarjs/cognitive-complexity


function TitleCell(_ref2) {
  let {
    column,
    colIndex,
    tableEl
  } = _ref2;
  const {
    sortField,
    sortOrder
  } = tableEl.state;
  const isCurrSortField = sortField === column.field;
  const mouseHandlers = column.tooltip ? {
    mouseenter: ev => tableEl.helpers.handleHeaderMouseEnter(ev, column),
    mouseleave: tableEl.helpers.handleHeaderMouseLeave
  } : {};
  return jsx("div", {
    sel: '.mp-table-cell.title-cell',
    class: {
      'mp-table-sortable-column': column.isSortable,
      'mp-checkbox-cell': column.type === "checkbox",
      'mp-table-segment-name': column.field === "header",
      'first-in-group': column.first && column.type !== "checkbox",
      'last-in-group': column.last && column.type !== "checkbox",
      'overall-conversion-header': column.field === "overall",
      'last-column': column.rightmost
    },
    on: {
      click: () => tableEl.helpers.handleHeaderTitleClick(column)
    },
    style: Object.assign({}, tableEl.helpers.hasClickInteraction(column) ? {
      cursor: "pointer"
    } : {}),
    attrs: {
      title: column.label || ""
    }
  }, column.type === "checkbox" ? // checkbox-wrapper so we can increase click surface area
  jsx("div", {
    sel: '.mp-table-checkbox-wrapper',
    style: {
      pointerEvents: tableEl.attr("multi-select") ? "" : "none"
    },
    on: {
      click: tableEl.helpers.toggleAllSelected
    }
  }, tableEl.attr("multi-select") ? (() => {
    const {
      numSelected,
      allSelected
    } = tableEl.helpers.getSelectionStats();
    return jsx(CheckboxEl, {
      attrs: {
        checked: allSelected,
        'unchecked-icon': numSelected > 0 ? "dash" : ""
      }
    });
  })() : null) : null, column.title ? jsx("div", {
    sel: '.mp-table-title-label',
    class: {
      'mp-table-title-currently-sorted-field': isCurrSortField,
      'mp-table-title-italics': column.italics
    },
    on: mouseHandlers
  }, jsx(ColumnTitle, {
    column: column,
    context: {
      colIndex,
      title: column.title
    }
  })) : null, column.icon ? jsx("div", {
    sel: '.mp-table-title-cell-icon',
    class: {
      'mp-table-title-currently-sorted-field': isCurrSortField
    },
    on: mouseHandlers
  }, jsx(SvgIconEl, {
    attrs: {
      icon: column.icon
    }
  })) : null, column.isSortable ? jsx("div", {
    sel: '.mp-table-sort-controls'
  }, isCurrSortField ? sortOrder === 1 ? jsx(SvgIconEl, {
    attrs: {
      icon: "arrow-up"
    }
  }) : jsx(SvgIconEl, {
    attrs: {
      icon: "arrow-down"
    }
  }) : null) : null);
}

function CellTypeText(_ref3) {
  let {
    context
  } = _ref3;
  return context.label;
}
/**
 * Positions a header-cell tooltip correctly.
 *
 * The default mp-tooltip does some magic to position itself which works for
 * most cases, but not for our sticky cells in a scrolling container.
 *
 * This mixin renders the tooltip outside of the table scroll container and on top of
 * the cell it's meant to annotate. Then, mp-tooltip does its normal positioning.
 */


function TitleTooltip(_ref4) {
  let {
    tooltip
  } = _ref4;

  if (!tooltip) {
    return null;
  }

  const {
    left,
    text,
    top
  } = tooltip;
  return jsx("div", {
    sel: '.position-tooltip',
    style: {
      left: "".concat(left, "px"),
      top: "".concat(top, "px"),
      position: "absolute"
    }
  }, jsx(TooltipEl, {
    attrs: {
      'max-width': "250px",
      placement: Placement.Bottom,
      'always-show': true
    }
  }, text));
}

function CellTypeCheckbox(_ref5) {
  let {
    item,
    context,
    tableEl
  } = _ref5;
  const options = tableEl.helpers.getCheckboxState(item);
  return !context.isComparison ? jsx("div", {
    sel: '.mp-table-checkbox-wrapper',
    class: {
      disabled: options.attrs.disabled
    },
    on: {
      click: ev => !options.attrs.disabled && tableEl.helpers.toggleSelected(ev, item)
    }
  }, jsx(CheckboxEl, {
    attrs: options.attrs
  }), options.selectDisabledReason && options.attrs.disabled ? jsx(TooltipEl, {
    attrs: {
      placement: Placement.Right
    }
  }, options.selectDisabledReason) : null) : null;
} //#endregion


function getWidth(cols, gap) {
  return sumBy(cols, c => c.width) + gap * cols.length - 1;
}

function getTemplateWidths(cols) {
  return cols.map(c => "".concat(c.width, "px")).join(" ");
} // eslint-disable-next-line sonarjs/cognitive-complexity


export function template() {
  const tableTheme = this.getTheme();
  const gap = tableTheme.cellBorders === "gap" ? 5 : 0;
  const [fixedCols, dataCols] = partition(this.helpers.getColumnsToDisplay(), col => col.sticky);
  const rows = this.helpers.getRowsToDisplay();
  const fixedColumnWidth = getWidth(fixedCols, gap);
  const dataColumnWidth = getWidth(dataCols, gap);
  return (// wrap the table in order to overlay tooltips on the scrollbox
    jsx("div", {
      sel: '.wrapper'
    }, jsx("div", {
      sel: '.mp-table',
      style: {
        '--data-table-columns': "".concat(fixedColumnWidth, "px ").concat(dataColumnWidth, "px"),
        '--fixed-column-sizes': getTemplateWidths(fixedCols),
        '--data-column-sizes': getTemplateWidths(dataCols),
        '--gap': "".concat(gap, "px"),
        '--height': this.attr("max-body-height"),
        '--first-column-group-width': "".concat(fixedColumnWidth + gap, "px")
      },
      class: {
        'use-borders': tableTheme.cellBorders === "line",
        'header-shadow': tableTheme.cellHeaderShadow,
        'rounded-cells': tableTheme.cellShape === "round",
        'justify-cells-within-group': tableTheme.justifyCellsWithinGroup,
        'cell-outline-on-hover': tableTheme.cellBorderHover === "outline"
      },
      on: {
        scroll: this.helpers.handleScroll
      }
    }, jsx("div", {
      sel: '.table-header.mp-data-table-grid'
    }, jsx("div", {
      sel: '.fixed-columns-outer'
    }, fixedCols.map((col, i) => jsx(TitleCell, {
      column: col,
      colIndex: i,
      tableEl: this
    }))), jsx("div", {
      sel: '.data-columns-outer'
    }, dataCols.map((col, i) => jsx(TitleCell, {
      column: col,
      colIndex: i,
      tableEl: this
    })))), rows.map((row, i) => jsx(RowGroup, {
      row: row,
      rowIdx: i,
      tableEl: this
    })), jsx("div", {
      hook: {
        insert: vnode => this.helpers.setIntersectionEl(vnode.elm)
      },
      style: {
        position: "relative",
        bottom: "400px",
        width: "100%"
      }
    })), jsx(TitleTooltip, {
      tooltip: this.state.cellToolTip
    }))
  );

  function RowGroup(_ref6) {
    let {
      row,
      rowIdx,
      tableEl
    } = _ref6;
    const isRowGroupEnabled = Array.isArray(row);
    const rows = isRowGroupEnabled ? row : [row];
    const currRowContexts = tableEl.helpers.getCellRenderContexts(tableEl.helpers.getColumnsToDisplay(), rows[0], rowIdx, rows.length);
    return jsx("div", {
      sel: '.row.mp-data-table-grid'
    }, rows.map((row, rowSubIdx) => jsx(null, null, jsx("div", {
      sel: '.fixed-columns-outer'
    }, fixedCols.map((col, i) => jsx(Cell, {
      column: col,
      row: row,
      rowIdx: isRowGroupEnabled ? rowSubIdx : rowIdx,
      columnIdx: i,
      tableEl: tableEl,
      contexts: currRowContexts
    }))), jsx("div", {
      sel: '.data-columns-outer'
    }, dataCols.map((col, i) => jsx(Cell, {
      column: col,
      row: row,
      rowIdx: isRowGroupEnabled ? rowSubIdx : rowIdx,
      columnIdx: i + fixedCols.length,
      tableEl: tableEl,
      contexts: currRowContexts
    }))))));
  }

  function Cell(_ref7) {
    let {
      row,
      rowIdx,
      column,
      columnIdx,
      tableEl,
      contexts
    } = _ref7;
    const context = contexts[columnIdx];
    const cellItem = tableEl.helpers.getCellItem(row, columnIdx);
    const evenRow = rowIdx % 2 === 0;
    const empty = cellItem === null;
    const bgColor = cellItem && cellItem.backgroundColor || column.backgroundColor || null;
    const notFocusable = !tableTheme.cellFocusable || tableTheme.cellFocusable && empty;
    let cellContent;

    if (column.type === "checkbox") {
      cellContent = jsx(CellTypeCheckbox, {
        context: context,
        item: row,
        tableEl: tableEl
      });
    } else {
      cellContent = (column.cellRenderer || CellTypeText)({
        item: row,
        context
      });
    }

    return jsx("div", {
      sel: '.mp-table-cell',
      class: {
        'first-in-group': column.first && column.type !== "checkbox",
        'last-in-group': column.last && column.type !== "checkbox",
        'last-column': column.rightmost,
        'mp-table-segment-name': column.field === "header",
        'mp-checkbox-cell': column.type === "checkbox",
        'first-in-pair': tableEl.attr("has-comparison") && evenRow,
        'striped-row': tableTheme.cellBackground === "row-stripe" && !evenRow,
        'first-row': rowIdx === 0,
        'non-numeric': column.type !== "number",
        'is-empty': empty,
        selectable: column.isSelectable
      },
      attrs: Object.assign({}, notFocusable ? {} : {
        tabindex: 0
      }),
      style: {
        'background-color': bgColor,
        color: cellItem ? cellItem.textColor : null,
        cursor: cellItem && cellItem.clickable ? "pointer" : "auto"
      },
      on: {
        click: ev => tableEl.helpers.handleCellClick(ev, row, column.field, context)
      }
    }, cellContent, cellItem && cellItem.tooltip ? (() => {
      const placement = columnIdx >= 8 ? Placement.Left : Placement.Right;
      return jsx(TooltipEl, {
        attrs: {
          placement
        },
        style: {
          'z-index': "200"
        }
      }, cellItem.tooltip);
    })() : null);
  }
}